import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Radio, Map, FileText, Wind, Compass, DollarSign, AlertTriangle, X, ClipboardList, ExternalLink } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function PilotInfo() {
  const {
    t
  } = useLanguage();
  const tabs = [{
    id: 'frequencies',
    label: t.pilot.tabs.frequencies,
    icon: Radio
  }, {
    id: 'circuits',
    label: t.pilot.tabs.circuits,
    icon: Compass
  }, {
    id: 'fees',
    label: t.pilot.tabs.fees,
    icon: DollarSign
  }, {
    id: 'arrival-form',
    label: t.pilot.tabs.arrivalForm,
    icon: ClipboardList,
    externalUrl: 'https://forms.gle/jREeEspwa3jHGTkF9'
  }, {
    id: 'info',
    label: t.pilot.tabs.info,
    icon: FileText
  }];
  const [activeTab, setActiveTab] = useState('frequencies');
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  useEffect(() => {
    if (lightboxImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [lightboxImage]);
  const handleTabClick = (tab: (typeof tabs)[number]) => {
    if ('externalUrl' in tab && tab.externalUrl) {
      window.open(tab.externalUrl, '_blank', 'noopener,noreferrer');
    } else {
      setActiveTab(tab.id);
    }
  };
  return <section id="pilot-info" className="py-24 bg-[#0a1628]" data-id="element-197" data-mp="28u5fixv5r9r6">
      <div className="max-w-5xl mx-auto px-6" data-id="element-198" data-mp="28wrtpgck0nid">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-199" data-mp="2dsyvcwxx3cui">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-200" data-mp="3ta0ltrqsk2z2">
            {t.pilot.title}
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto mb-6" data-id="element-201" data-mp="1u4pd3wzobtnq">
            {t.pilot.subtitle}
          </p>
          <a href="https://aim.rlp.cz/vfrmanual/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-[#38bdf8] border-2 border-[#38bdf8] rounded-none hover:bg-[#38bdf8] hover:text-[#0a1628] transition-all duration-300" data-id="element-202" data-mp="1p6fibnhv2v2n">
            <ExternalLink size={16} data-id="element-203" data-mp="16gpop3pu4fcb" />
            {t.pilot.vfrManual}
          </a>
        </motion.div>

        {/* Tabs Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-8" data-id="element-204" data-mp="1k8w24i1ls882">
          {tabs.map(tab => {
          const isExternal = 'externalUrl' in tab && tab.externalUrl;
          return <button key={tab.id} onClick={() => handleTabClick(tab)} className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${isExternal ? 'bg-[#f87171] text-white hover:bg-[#ef4444]' : activeTab === tab.id ? 'bg-[#38bdf8] text-[#0a1628]' : 'bg-[#0f1d35] text-white/60 hover:bg-[#1e3a5f] hover:text-white'}`} data-id="element-205" data-mp="rqi5gls06sak">
                <tab.icon size={16} data-id="element-206" data-mp="2pgyofn14oisr" />
                {tab.label}
                {isExternal && <ExternalLink size={12} className="ml-0.5 opacity-60" data-id="element-207" data-mp="2uegoenx63nom" />}
              </button>;
        })}
        </div>

        {/* Tab Content */}
        <div className="bg-[#0f1d35] border border-white/5 rounded-2xl p-8 md:p-12 min-h-[400px]" data-id="element-208" data-mp="3m43f9xpngalm">
          <AnimatePresence mode="wait" data-id="element-209" data-mp="384tx4shvv6f0">
            {activeTab === 'frequencies' && <motion.div key="frequencies" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3
          }} data-id="element-210" data-mp="3d3ndqtsgvdje">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-211" data-mp="286w67u4xc4jm">
                  <Radio className="text-[#38bdf8]" data-id="element-212" data-mp="237rwn2e384sr" /> {t.pilot.freq.title}
                </h3>

                {/* Desktop Table */}
                <div className="hidden md:block overflow-hidden rounded-xl border border-white/10 mb-8" data-id="element-213" data-mp="2zyc1eckjqzlg">
                  <table className="w-full text-left" data-id="element-214" data-mp="2vxicbfvdbeqz">
                    <thead className="bg-[#0a1628]" data-id="element-215" data-mp="2qxdu9letabyn">
                      <tr data-id="element-216" data-mp="2qw2ppjdjl06h">
                        <th className="p-4 text-white/50 font-medium text-sm uppercase tracking-wider" data-id="element-217" data-mp="2qzgsutu15dez">
                          {t.pilot.freq.station}
                        </th>
                        <th className="p-4 text-white/50 font-medium text-sm uppercase tracking-wider" data-id="element-218" data-mp="1fjs5w2gs0ee6">
                          {t.pilot.freq.callsign}
                        </th>
                        <th className="p-4 text-white/50 font-medium text-sm uppercase tracking-wider" data-id="element-219" data-mp="10gicxhrx98aq">
                          {t.pilot.freq.frequency}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5" data-id="element-220" data-mp="1abae0vkpqda9">
                      <tr className="bg-[#0f1d35]" data-id="element-221" data-mp="1f4vaukakl17v">
                        <td className="p-4 text-white font-medium" data-id="element-222" data-mp="1j5ox798d67yn">RADIO</td>
                        <td className="p-4 text-white/70" data-id="element-223" data-mp="1j7rn7t9io13d">HOŘOVICE RADIO</td>
                        <td className="p-4 text-[#38bdf8] font-mono font-bold text-lg" data-id="element-224" data-mp="1o370aizp9syq">
                          135.580 MHz
                        </td>
                      </tr>
                      <tr className="bg-[#0f1d35]" data-id="element-225" data-mp="1y1wjcf3c1jgk">
                        <td className="p-4 text-white font-medium" data-id="element-226" data-mp="3uv7rxhiv5vj7">
                          {t.pilot.freq.emergency}
                        </td>
                        <td className="p-4 text-white/70" data-id="element-227" data-mp="3ppvafpcb86u">
                          {t.pilot.freq.general}
                        </td>
                        <td className="p-4 text-red-400 font-mono font-bold text-lg" data-id="element-228" data-mp="3rvpk7kbmur0b">
                          121.500 MHz
                        </td>
                      </tr>
                      <tr className="bg-[#0f1d35]" data-id="element-229" data-mp="5nzkxs1iwhzj">
                        <td className="p-4 text-white font-medium" data-id="element-230" data-mp="msgo2yla0xm">ATIS</td>
                        <td className="p-4 text-white/70" data-id="element-231" data-mp="poapg7bg5gs">
                          {t.pilot.freq.prague}
                        </td>
                        <td className="p-4 text-white/40 font-mono" data-id="element-232" data-mp="9orxau5oz301">
                          122.160 MHz
                        </td>
                      </tr>
                      <tr className="bg-[#0f1d35]" data-id="element-233" data-mp="oeqwn08zgkh3">
                        <td className="p-4 text-white font-medium" data-id="element-234" data-mp="jf4gahxuyo42">VOLMET</td>
                        <td className="p-4 text-white/70" data-id="element-235" data-mp="xct1xrzr1za6">-</td>
                        <td className="p-4 text-white/40 font-mono" data-id="element-236" data-mp="xb849ti52083">
                          125.525 MHz
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden space-y-3 mb-8" data-id="element-237" data-mp="1gyl5pc25qenh">
                  <div className="bg-[#0a1628] rounded-xl border border-white/5 p-4" data-id="element-238" data-mp="130efmi3g9iyd">
                    <div className="flex justify-between items-start mb-1" data-id="element-239" data-mp="17y6i261q0ngt">
                      <span className="text-white font-medium" data-id="element-240" data-mp="180iu62kss3ly">RADIO</span>
                      <span className="text-[#38bdf8] font-mono font-bold" data-id="element-241" data-mp="t8h3n94mamyl">
                        135.580 MHz
                      </span>
                    </div>
                    <span className="text-white/50 text-sm" data-id="element-242" data-mp="ka52wea1e8sf">
                      HOŘOVICE RADIO
                    </span>
                  </div>
                  <div className="bg-[#0a1628] rounded-xl border border-white/5 p-4" data-id="element-243" data-mp="o8cjn94i3a3m">
                    <div className="flex justify-between items-start mb-1" data-id="element-244" data-mp="abpjkxh943th">
                      <span className="text-white font-medium" data-id="element-245" data-mp="f8pj0pjli8bm">
                        {t.pilot.freq.emergency}
                      </span>
                      <span className="text-red-400 font-mono font-bold" data-id="element-246" data-mp="upo0e50ydpof">
                        121.500 MHz
                      </span>
                    </div>
                    <span className="text-white/50 text-sm" data-id="element-247" data-mp="lpso1wvnvomt">
                      {t.pilot.freq.general}
                    </span>
                  </div>
                  <div className="bg-[#0a1628] rounded-xl border border-white/5 p-4" data-id="element-248" data-mp="btplipwwdnzc">
                    <div className="flex justify-between items-start mb-1" data-id="element-249" data-mp="72fvho7fsimx">
                      <span className="text-white font-medium" data-id="element-250" data-mp="70cz8mfoxt9r">ATIS</span>
                      <span className="text-white/50 font-mono" data-id="element-251" data-mp="23w9xowr98v3">
                        122.160 MHz
                      </span>
                    </div>
                    <span className="text-white/50 text-sm" data-id="element-252" data-mp="3p7svnaxb1rzw">
                      {t.pilot.freq.prague}
                    </span>
                  </div>
                  <div className="bg-[#0a1628] rounded-xl border border-white/5 p-4" data-id="element-253" data-mp="3k9rewwncq4rl">
                    <div className="flex justify-between items-start mb-1" data-id="element-254" data-mp="1zqf902bha3sc">
                      <span className="text-white font-medium" data-id="element-255" data-mp="24oyxa9djnrnv">VOLMET</span>
                      <span className="text-white/50 font-mono" data-id="element-256" data-mp="29pean0xtsz5u">
                        125.525 MHz
                      </span>
                    </div>
                    <span className="text-white/50 text-sm" data-id="element-257" data-mp="2dpx38x7jm993">-</span>
                  </div>
                </div>

                <div className="p-4 bg-[#38bdf8]/10 border border-[#38bdf8]/20 rounded-lg" data-id="element-258" data-mp="1gycrkor0ijh1">
                  <p className="text-[#38bdf8] text-sm" data-id="element-259" data-mp="1lxfv24rcghmw">
                    <strong data-id="element-260" data-mp="1q02ryk2vgsqy">{t.pilot.freq.noteLabel}</strong>{' '}
                    {t.pilot.freq.note}
                  </p>
                </div>
              </motion.div>}

            {activeTab === 'circuits' && <motion.div key="circuits" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3
          }} data-id="element-261" data-mp="y9pmw7cqzw3z">
                <div className="grid md:grid-cols-2 gap-8" data-id="element-262" data-mp="3ahgf60sof5v5">
                  <div data-id="element-263" data-mp="3aeu965lfoyf1">
                    <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-264" data-mp="2dr7cc6kg7e12">
                      <Wind className="text-[#38bdf8]" data-id="element-265" data-mp="2hs9fgyhhka8b" /> {t.pilot.runway.title}
                    </h3>
                    <div className="bg-[#0a1628] p-6 rounded-xl border border-white/5 mb-6" data-id="element-266" data-mp="2mr3yo1jd80sy">
                      <div className="flex justify-between items-center mb-4" data-id="element-267" data-mp="2ri2ivc7h5j1h">
                        <span className="text-xl font-bold text-white" data-id="element-268" data-mp="2rk6r26cep47k">
                          RWY 06 / 24
                        </span>
                        <span className="px-3 py-1 bg-[#38bdf8]/20 text-[#38bdf8] rounded text-sm font-bold" data-id="element-269" data-mp="1z0zunt29lz1f">
                          {t.pilot.runway.surface}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm text-white/70" data-id="element-270" data-mp="1mjyu580xui98">
                        <p data-id="element-271" data-mp="1hjkv2fhbeinc">
                          {t.pilot.runway.dimensions}:{' '}
                          <strong data-id="element-272" data-mp="1djagwfjdclxe">1170 x 30 m</strong>
                        </p>
                        <p data-id="element-273" data-mp="18jo2w3vpv1nj">
                          {t.pilot.runway.strength}:{' '}
                          <strong data-id="element-274" data-mp="18lhc9qxprzz8">PCN 25/R/B/U</strong>
                        </p>
                        <p data-id="element-275" data-mp="13rmuit1o5v2s">
                          {t.pilot.runway.slope}: <strong data-id="element-276" data-mp="yt370o7fgb8b">0.18%</strong>{' '}
                          {t.pilot.runway.slopeNote}
                        </p>
                        <p className="text-yellow-400/80" data-id="element-277" data-mp="twcto8n0btql">
                          {t.pilot.runway.warning}
                        </p>
                      </div>
                    </div>

                    {/* Runway Diagram Thumbnail */}
                    <div className="relative overflow-hidden rounded-xl border border-white/10 cursor-pointer group" onClick={() => setLightboxImage('https://cdn.magicpatterns.com/uploads/u3egShru5eatQbgsvmYZ6f/lkhv_adc.webp')} data-id="element-278" data-mp="b6ps82np9mdb">
                      <img src="https://cdn.magicpatterns.com/uploads/u3egShru5eatQbgsvmYZ6f/lkhv_adc.webp" alt={t.pilot.runway.mapAlt} className="w-full h-auto object-contain bg-white/90 p-2 transition-transform duration-500 group-hover:scale-105" data-id="element-279" data-mp="twz27ypqaifk" />
                      <div className="absolute inset-0 bg-[#0a1628]/40 transition-opacity duration-300 group-hover:opacity-0" data-id="element-280" data-mp="p4xvstf8rlxg" />
                    </div>
                  </div>

                  <div data-id="element-281" data-mp="1hmtg8jlsad1e">
                    <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-282" data-mp="1hnbko3ilnxke">
                      <Compass className="text-[#38bdf8]" data-id="element-283" data-mp="32bnqgykntc5q" />{' '}
                      {t.pilot.circuits.title}
                    </h3>
                    <ul className="space-y-4" data-id="element-284" data-mp="2xc1eesgki1yi">
                      <li className="flex items-start gap-3 text-white/80" data-id="element-285" data-mp="3b47ihg6g4ihl">
                        <span className="w-2 h-2 bg-[#38bdf8] rounded-full mt-2" data-id="element-286" data-mp="373xajxz3fi03" />
                        <span data-id="element-287" data-mp="2je32rutmsjmp">
                          <strong data-id="element-288" data-mp="2jdb76k01alwf">{t.pilot.circuits.northOnly}</strong>{' '}
                          {t.pilot.circuits.northOnlyRest}
                          <br data-id="element-289" data-mp="2eguhhld6rzh8" />
                          <span className="text-sm text-white/50" data-id="element-290" data-mp="2serictyoas1p">
                            {t.pilot.circuits.turnNote}
                          </span>
                        </span>
                      </li>
                      <li className="flex items-start gap-3 text-white/80" data-id="element-291" data-mp="7oa7j50gn6i7">
                        <span className="w-2 h-2 bg-[#38bdf8] rounded-full mt-2" data-id="element-292" data-mp="7qd8gk35ik1p" />
                        <span data-id="element-293" data-mp="2p78q2b2pwio">
                          {t.pilot.circuits.altitudeLabel}{' '}
                          <strong className="text-white" data-id="element-294" data-mp="y1gjz098s1u1">2 300 ft AMSL</strong>{' '}
                          (QNH)
                          <br data-id="element-295" data-mp="130jqyq8s8i4n" />
                          <span className="text-sm text-white/50" data-id="element-296" data-mp="17xa5h94xv5ny">
                            {t.pilot.circuits.altitudeNote}
                          </span>
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="mt-8 p-6 bg-red-500/10 border border-red-500/20 rounded-xl" data-id="element-297" data-mp="1lxb5ynhakb2w">
                  <h4 className="text-red-400 font-bold flex items-center gap-2 mb-2" data-id="element-298" data-mp="1qob0xn6uoj2r">
                    <AlertTriangle size={20} data-id="element-299" data-mp="1qqnkg4s9b1hx" /> {t.pilot.noise.title}
                  </h4>
                  <ul className="list-disc list-inside text-white/70 space-y-1 text-sm" data-id="element-300" data-mp="1vph0a3v0n4l9">
                    {t.pilot.noise.items.map(item => <li key={item} data-id="element-301" data-mp="24naoerimmrxc">{item}</li>)}
                  </ul>
                </div>
              </motion.div>}

            {activeTab === 'fees' && <motion.div key="fees" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3
          }} data-id="element-302" data-mp="3l49s37eypd4k">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-303" data-mp="7rpv444wz41k">
                  <DollarSign className="text-[#38bdf8]" data-id="element-304" data-mp="2wtska21wya7" /> {t.pilot.fees.title}
                </h3>

                <div className="grid md:grid-cols-2 gap-6" data-id="element-305" data-mp="3u33mcr4tlrey">
                  <div className="bg-[#0a1628] border border-white/5 rounded-xl overflow-hidden" data-id="element-306" data-mp="vg946lvsfkup">
                    <div className="p-4 bg-white/5 border-b border-white/5 font-bold text-white" data-id="element-307" data-mp="qnfzfy0bkcim">
                      {t.pilot.fees.landing}
                    </div>
                    <div className="p-4 space-y-4" data-id="element-308" data-mp="2oxokbgd74w6g">
                      <div className="flex justify-between items-center border-b border-white/5 pb-2" data-id="element-309" data-mp="2oxer7jwuu8wj">
                        <span className="text-white/70" data-id="element-310" data-mp="2b69qeehr3akm">
                          {t.pilot.fees.ultralight}
                        </span>
                        <span className="text-[#38bdf8] font-bold" data-id="element-311" data-mp="218lsxgsrfoyr">120 Kč</span>
                      </div>
                      <div className="flex justify-between items-center border-b border-white/5 pb-2" data-id="element-312" data-mp="267ygy4vdb22x">
                        <span className="text-white/70" data-id="element-313" data-mp="1s875aasbpxs3">
                          {t.pilot.fees.upTo2000}
                        </span>
                        <span className="text-[#38bdf8] font-bold" data-id="element-314" data-mp="1x7abhyk1ia1n">150 Kč</span>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-315" data-mp="3mmgh0njzxq3d">
                        <span className="text-white/70" data-id="element-316" data-mp="3rjgp140ep2x4">
                          {t.pilot.fees.over2000}
                        </span>
                        <span className="text-[#38bdf8] font-bold" data-id="element-317" data-mp="5h7vsj79y9bk">
                          270 Kč{' '}
                          <span className="text-xs text-white/40 font-normal" data-id="element-318" data-mp="iekmxfgmuek">
                            + 140 Kč/t
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#0a1628] border border-white/5 rounded-xl overflow-hidden h-min" data-id="element-319" data-mp="o8imaz938k2x">
                    <div className="p-4 bg-white/5 border-b border-white/5 font-bold text-white" data-id="element-320" data-mp="ja7e0syfyhmt">
                      {t.pilot.fees.other}
                    </div>
                    <div className="p-4 space-y-4" data-id="element-321" data-mp="t8dbmyd7t1lv">
                      <div className="flex justify-between items-center border-b border-white/5 pb-2" data-id="element-322" data-mp="t6sj1eagnsqn">
                        <span className="text-white/70" data-id="element-323" data-mp="2z3xzt52vfx8r">
                          {t.pilot.fees.parking}
                        </span>
                        <span className="text-[#38bdf8] font-bold" data-id="element-324" data-mp="3jlq228mcys5w">140 Kč</span>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-325" data-mp="3ojhqgsnsnw5c">
                        <span className="text-white/70" data-id="element-326" data-mp="3tb9kxea2mm0r">
                          {t.pilot.fees.unauthorized}
                        </span>
                        <span className="text-red-400 font-bold" data-id="element-327" data-mp="15xc7uns9max">
                          {t.pilot.fees.from} 10 000 Kč
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="mt-6 text-sm text-white/50 text-center" data-id="element-328" data-mp="39n0j0ciq71o2">
                  {t.pilot.fees.note}
                </p>
              </motion.div>}

            {activeTab === 'info' && <motion.div key="info" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3
          }} data-id="element-329" data-mp="ox1mr0b1mh1a">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-330" data-mp="u8ghv51mx4dl">
                  <FileText className="text-[#38bdf8]" data-id="element-331" data-mp="q7wnuaccygvo" /> {t.pilot.info.title}
                </h3>

                <div className="space-y-6" data-id="element-332" data-mp="lawiy2r8vk65">
                  <div className="bg-[#0a1628] p-6 rounded-xl border border-white/5" data-id="element-333" data-mp="ga8jqtsv3x0u">
                    <h4 className="text-white font-bold mb-2" data-id="element-334" data-mp="gcl7jnlax1lt">
                      {t.pilot.info.atzTitle}
                    </h4>
                    <p className="text-white/70 text-sm mb-2" data-id="element-335" data-mp="7aby711rrea0">
                      {t.pilot.info.atzHorizontal}{' '}
                      <strong data-id="element-336" data-mp="25nvhkgl8aace">3 NM (5,5 km)</strong> {t.pilot.info.atzFromArp}
                      <br data-id="element-337" data-mp="21nbk1bwqt4pq" />
                      {t.pilot.info.atzVertical}{' '}
                      <strong data-id="element-338" data-mp="894nkv6w33vs">4 000 ft AMSL</strong> (1200 m).
                    </p>
                    <p className="text-white/50 text-xs" data-id="element-339" data-mp="47ri4m7pl2pe">
                      {t.pilot.info.atzNote}
                    </p>
                  </div>

                  <div className="bg-[#0a1628] p-6 rounded-xl border border-white/5" data-id="element-340" data-mp="3vl5oaacmpgs4">
                    <h4 className="text-white font-bold mb-2" data-id="element-341" data-mp="3qmaw4hsrufvc">
                      {t.pilot.info.alternatesTitle}
                    </h4>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-id="element-342" data-mp="3lm6cwk0hb71f">
                      <li className="text-white/70" data-id="element-343" data-mp="3gnmswp5g45bp">
                        <strong className="text-white" data-id="element-344" data-mp="3cn2t1dthmnlv">Příbram (LKPM)</strong>
                        <br data-id="element-345" data-mp="37ohstee8k3xf" />
                        118.755 MHz, 20 km
                      </li>
                      <li className="text-white/70" data-id="element-346" data-mp="1aw7vagfq0eci">
                        <strong className="text-white" data-id="element-347" data-mp="1auwumphknmpm">Beroun (LKBU)</strong>
                        <br data-id="element-348" data-mp="15wvbk4k5bne5" />
                        134.305 MHz, 25 km
                      </li>
                      <li className="text-white/70" data-id="element-349" data-mp="1dhflloqswdq3">
                        <strong className="text-white" data-id="element-350" data-mp="1dejm3vzb1gzj">Kladno (LKKL)</strong>
                        <br data-id="element-351" data-mp="1ie62geafjdck" />
                        123.480 MHz, 30 km
                      </li>
                      <li className="text-white/70" data-id="element-352" data-mp="1n6z6ezy2qhnl">
                        <strong className="text-white" data-id="element-353" data-mp="1s621pso959id">Rakovník (LKRA)</strong>
                        <br data-id="element-354" data-mp="uhrxr84wobom" />
                        123.510 MHz, 30 km
                      </li>
                    </ul>
                  </div>

                  <div className="bg-[#0a1628] p-6 rounded-xl border border-white/5" data-id="element-355" data-mp="zho1r7dls346">
                    <h4 className="text-white font-bold mb-2" data-id="element-356" data-mp="14fptegp3har9">
                      {t.pilot.info.altimeterTitle}
                    </h4>
                    <p className="text-white/70 text-sm" data-id="element-357" data-mp="18ivwxgktonu1">
                      {t.pilot.info.altimeterText}{' '}
                      <strong data-id="element-358" data-mp="2fxh5lpvofyvq">381 m (1 249 ft) AMSL</strong>
                    </p>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Lightbox for runway diagram */}
      <AnimatePresence data-id="element-359" data-mp="fs3lko8li6e7">
        {lightboxImage && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 md:p-8" onClick={() => setLightboxImage(null)} data-id="element-360" data-mp="tsedluln5r0y">
            <button className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-50 bg-black/20 p-2 rounded-full backdrop-blur-md" onClick={e => {
          e.stopPropagation();
          setLightboxImage(null);
        }} data-id="element-361" data-mp="av3fwefnr5nm">
              <X size={32} data-id="element-362" data-mp="39hpu5hajph8t" />
            </button>
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="relative max-w-5xl max-h-[90vh] w-full flex items-center justify-center" onClick={e => e.stopPropagation()} data-id="element-363" data-mp="1p1hx3xn3106k">
              <img src={lightboxImage} alt={t.pilot.runway.mapAlt} className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl bg-white p-2" data-id="element-364" data-mp="1yqi2bys6grt7" />
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}